// CB:BEGIN admin-reviews-page
import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../lib/useApi';

interface Review {
  id: string;
  projectId: string;
  status: 'SUBMITTED' | 'HIDDEN' | 'FLAGGED' | 'DRAFT';
  submittedAt: string | null;
  overallContractorRating: number | null;
  overallProjectRating: number | null;
  easeOfUseRating: number | null;
  publicReviewText: string | null;
  privateFeedbackText: string | null;
  wouldRecommendContractor: boolean | null;
  project: { id: string; address: string; status: string };
  contractor: { id: string; company: string | null; user: { email: string } };
  homeowner: { id: string; user: { email: string } };
}

interface ReviewsResponse {
  reviews: Review[];
  total: number;
}

const STATUS_COLORS: Record<string, string> = {
  SUBMITTED: '#059669',
  HIDDEN: '#6b7280',
  FLAGGED: '#d97706',
  DRAFT: '#9ca3af',
};

function StarDisplay({ value }: { value: number | null }) {
  if (!value) return <span style={{ color: '#9ca3af' }}>—</span>;
  return (
    <span style={{ color: '#f59e0b' }}>
      {'★'.repeat(value)}{'☆'.repeat(5 - value)}
      <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 4 }}>{value}/5</span>
    </span>
  );
}

export default function AdminReviewsPage() {
  const { get, patch } = useApi();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [minRating, setMinRating] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (statusFilter) params.set('status', statusFilter);
      if (minRating) params.set('minRating', minRating);
      const data = await get<ReviewsResponse>(`/api/admin/reviews?${params}`);
      setReviews(data.reviews || []);
      setTotal(data.total || 0);
    } catch (e: any) {
      setError(e?.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [get, statusFilter, minRating, offset]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id: string, status: string) => {
    setUpdatingId(id);
    try {
      await patch(`/api/admin/reviews/${id}`, { status });
      setReviews(prev => prev.map(r => r.id === id ? { ...r, status: status as Review['status'] } : r));
    } catch (e: any) {
      alert(e?.message || 'Failed to update review');
    } finally {
      setUpdatingId(null);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20 }}>Review Management</h2>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setOffset(0); }}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
        >
          <option value="">All statuses</option>
          <option value="SUBMITTED">Submitted</option>
          <option value="FLAGGED">Flagged</option>
          <option value="HIDDEN">Hidden</option>
        </select>
        <select
          value={minRating}
          onChange={e => { setMinRating(e.target.value); setOffset(0); }}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
        >
          <option value="">Any rating</option>
          {[1,2,3,4,5].map(n => <option key={n} value={n}>≥ {n} stars</option>)}
        </select>
        <span style={{ alignSelf: 'center', color: '#6b7280', fontSize: 13 }}>{total} total</span>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: '#6b7280', padding: 24 }}>Loading…</div>
      ) : reviews.length === 0 ? (
        <div style={{ color: '#6b7280', padding: 24 }}>No reviews found.</div>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Project</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Homeowner</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Contractor</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Rating</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Status</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Date</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map(r => (
                <React.Fragment key={r.id}>
                  <tr
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: expandedId === r.id ? '#f0fdf4' : 'white' }}
                    onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                  >
                    <td style={{ padding: '10px 14px' }}>{r.project?.address || r.projectId.slice(-8)}</td>
                    <td style={{ padding: '10px 14px', color: '#6b7280' }}>{r.homeowner?.user?.email || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>{r.contractor?.company || r.contractor?.user?.email || '—'}</td>
                    <td style={{ padding: '10px 14px' }}><StarDisplay value={r.overallContractorRating} /></td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: STATUS_COLORS[r.status] || '#6b7280',
                        background: `${STATUS_COLORS[r.status]}18`,
                        padding: '2px 8px',
                        borderRadius: 12,
                      }}>
                        {r.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#6b7280' }}>
                      {r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '10px 14px' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {r.status !== 'FLAGGED' && (
                          <button
                            onClick={() => updateStatus(r.id, 'FLAGGED')}
                            disabled={updatingId === r.id}
                            style={{
                              padding: '3px 10px', fontSize: 11, borderRadius: 4,
                              border: '1px solid #d97706', background: 'white', color: '#d97706', cursor: 'pointer',
                            }}
                          >
                            Flag
                          </button>
                        )}
                        {r.status === 'HIDDEN' ? (
                          <button
                            onClick={() => updateStatus(r.id, 'SUBMITTED')}
                            disabled={updatingId === r.id}
                            style={{
                              padding: '3px 10px', fontSize: 11, borderRadius: 4,
                              border: '1px solid #059669', background: 'white', color: '#059669', cursor: 'pointer',
                            }}
                          >
                            Restore
                          </button>
                        ) : (
                          <button
                            onClick={() => updateStatus(r.id, 'HIDDEN')}
                            disabled={updatingId === r.id}
                            style={{
                              padding: '3px 10px', fontSize: 11, borderRadius: 4,
                              border: '1px solid #6b7280', background: 'white', color: '#6b7280', cursor: 'pointer',
                            }}
                          >
                            Hide
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === r.id && (
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      <td colSpan={7} style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 14 }}>
                          <div><span style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>Overall Contractor</span><br /><StarDisplay value={r.overallContractorRating} /></div>
                          <div><span style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>Overall Project</span><br /><StarDisplay value={r.overallProjectRating} /></div>
                          <div><span style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>Platform Ease</span><br /><StarDisplay value={r.easeOfUseRating} /></div>
                          <div><span style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>Would Recommend</span><br /><span style={{ fontWeight: 600, color: r.wouldRecommendContractor === true ? '#059669' : r.wouldRecommendContractor === false ? '#dc2626' : '#9ca3af' }}>{r.wouldRecommendContractor === null ? '—' : r.wouldRecommendContractor ? 'Yes' : 'No'}</span></div>
                        </div>
                        {r.publicReviewText && (
                          <div style={{ marginBottom: 10 }}>
                            <span style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Public Review</span>
                            <p style={{ margin: 0, color: '#374151', fontSize: 13, lineHeight: 1.5 }}>{r.publicReviewText}</p>
                          </div>
                        )}
                        {r.privateFeedbackText && (
                          <div style={{ background: '#fef3c7', padding: '8px 12px', borderRadius: 6 }}>
                            <span style={{ fontSize: 11, color: '#92400e', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Private Feedback (admin only)</span>
                            <p style={{ margin: 0, color: '#78350f', fontSize: 13, lineHeight: 1.5 }}>{r.privateFeedbackText}</p>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' }}>
          <button
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={currentPage === 1}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: 13 }}
          >
            ← Prev
          </button>
          <span style={{ alignSelf: 'center', fontSize: 13, color: '#6b7280' }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setOffset(offset + limit)}
            disabled={currentPage >= totalPages}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: 13 }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
// CB:END admin-reviews-page
